import React, { useMemo, useState } from "react";
import { C, font } from "../lib/styles.js";
import { DataContextTestProvider } from "../context/DataContext.jsx";
import Dashboard from "../apps/jobs/views/Dashboard.jsx";
import { getEqNames } from "../lib/equipment.js";
import { jobAccess } from "../apps/jobs/lib/jobAccess.js";
import { HOLD_JOBS, HOLD_QUOTES, HOLD_RECORDS, HOLD_ROSTER, holdsParams } from "./holdsSeed.js";

// No-login fixture for the Jobs Dashboard (week calendar / briefings / pipeline /
// revenue). Dashboard({navigate, user}) reads { jobs, savedQuotes } from
// useData() and derives caps from jobAccess(user.role); admin => revenue bar +
// all panels. Dates are computed relative to today so the week calendar always
// has jobs on it, whenever this runs.

const isoOffset = (n) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const JOBS = [
  { id: "j1", clientName: "Acme Arts LLC", jobId: "acmart_260615", status: "scheduled", captureDate: isoOffset(0), multiDay: false, sessions: [], grossRevenue: 8200, invoiced: false, paid: false, isStudio: true, locationVenue: "Studio A" },
  {
    id: "j2", clientName: "Vanguard Films", jobId: "vanfil_260616", status: "in_progress", multiDay: true,
    sessions: [
      { id: "s1", date: isoOffset(1), sessionType: "setup", callTime: "08:00", wrapTime: "12:00", isStudio: false, locationVenue: "Brooklyn Navy Yard" },
      { id: "s2", date: isoOffset(2), sessionType: "capture", callTime: "08:30", wrapTime: "17:00", isStudio: false, locationVenue: "Brooklyn Navy Yard" },
      { id: "s3", date: isoOffset(3), sessionType: "capture", callTime: "08:30", wrapTime: "17:00", isStudio: false, locationVenue: "Brooklyn Navy Yard" },
    ],
    grossRevenue: 21450, invoiced: false, paid: false,
  },
  { id: "j3", clientName: "JF Bouchard", jobId: "jfbouc_260618", status: "scheduled", captureDate: isoOffset(4), multiDay: false, sessions: [], grossRevenue: 3600, invoiced: false, paid: false, locationVenue: "Queens" },
  { id: "j4", clientName: "Northwind Studios", jobId: "northw_260530", status: "processing", captureDate: isoOffset(-3), multiDay: false, sessions: [], grossRevenue: 9800, invoiced: true, paid: false },
  { id: "j5", clientName: "Helios Media", jobId: "helios_260512", status: "delivered", captureDate: isoOffset(-20), multiDay: false, sessions: [], grossRevenue: 14200, invoiced: true, paid: true, paidDate: isoOffset(-9) },
];

const QUOTES = [
  { id: "q1", clientName: "Pending Co", jobStatus: "quoted", grossRevenue: 5000, lineItems: [] },
  { id: "q2", clientName: "Maybe Inc", jobStatus: "quoted", grossRevenue: 7500, lineItems: [] },
  // Exercises the (formerly stubbed) Follow-ups card: overdue → badge + row.
  { id: "q3", clientName: "Follow Up Films", contactName: "Nora Reyes", jobStatus: "quoted", total: 4200, followUpDate: isoOffset(-2), summary: "Awaiting reply on revised quote", lineItems: [] },
];

// ?e2e=dashboard&holds=1&role=admin|superadmin|crew|viewer (holds, 2026-09-04):
// OFF by default so the relative-date specs above stay untouched. With holds=1
// the jobs are the fixed October scenario in holdsSeed.js (the specs pin the
// clock to HOLDS_TODAY) and the context carries the hold records, the roster,
// and the live equipment map. The context always carries `holds`, `holdsLoading`,
// and `role` the way the role-aware DataProvider does (role is the jobAccess
// role, so "superadmin" stays as-is); only admins receive savedQuotes.
export default function E2EDashboardFixture() {
  const { holds, role } = holdsParams();
  const [jobs] = useState(holds ? HOLD_JOBS : JOBS);
  const user = { role, name: `E2E ${role}`, email: `${role}@e2e.test` };

  const value = useMemo(() => {
    const isAdmin = jobAccess(role).isAdmin;
    return {
      jobs,
      savedQuotes: !isAdmin ? [] : holds ? [...QUOTES, ...HOLD_QUOTES] : QUOTES,
      holds: holds ? HOLD_RECORDS : [],
      holdsLoading: false,
      role: jobAccess(role).role,
      crewRoster: holds ? HOLD_ROSTER : [],
      equipmentMap: getEqNames(),
    };
  }, [jobs, holds, role]);

  return (
    <DataContextTestProvider value={value}>
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: font, padding: 16 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 11, color: C.amber, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>
            Local test mode — Dashboard Fixture
          </div>
          <Dashboard navigate={() => {}} user={user} />
        </div>
      </div>
    </DataContextTestProvider>
  );
}
